import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../../config/api';
import PageHeader from '../../components/PageHeader';

interface Hazard {
  dimhazards_id: number;
  hazardname: string;
  hazardscategoryname: string | null;
}

interface RasffNotification {
  rasff_id: number;
  notif_id: number;
  reference: string | null;
  subject: string | null;
  hazard_names: string | null;
  ec_validation_date: string | null;
  product_category: string | null;
  dimhazards_id: number | null;
  dim_hazards: Hazard | null;
}

interface FoodAlert {
  food_alerts_id: number;
  source: string;
  external_id: string;
  title: string | null;
  description: string | null;
  alert_type: string | null;
  published_date: string | null;
  dimhazards_id: number | null;
  dim_hazards: Hazard | null;
}

interface UncategorizedResponse {
  notifications: RasffNotification[];
  total: number;
  limit: number;
  offset: number;
}

interface UncategorizedAlertsResponse {
  alerts: FoodAlert[];
  total: number;
  limit: number;
  offset: number;
}

type CategorizationTab = 'rasff' | 'food_alerts';

const baseUrl = API_BASE_URL;
const PAGE_SIZE = 50;

async function fetchHazards(): Promise<Hazard[]> {
  const res = await authFetch(`${baseUrl}/api/hazards`);
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.hazards;
}

async function fetchUncategorized(offset: number, search: string): Promise<UncategorizedResponse> {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (search.length >= 2) params.set('q', search);
  const res = await authFetch(`${baseUrl}/api/rasff-notifications/uncategorized?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function fetchUncategorizedAlerts(offset: number, search: string): Promise<UncategorizedAlertsResponse> {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (search.length >= 2) params.set('q', search);
  const res = await authFetch(`${baseUrl}/api/food-alerts/uncategorized?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function updateHazard(params: { rasffId: number; dimhazards_id: number }): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/rasff-notifications/${params.rasffId}/hazard`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dimhazards_id: params.dimhazards_id }),
  });
  if (!res.ok) throw new Error(await res.text());
}

async function updateAlertHazard(params: { alertId: number; dimhazards_id: number }): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/food-alerts/${params.alertId}/hazard`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dimhazards_id: params.dimhazards_id }),
  });
  if (!res.ok) throw new Error(await res.text());
}

async function createHazard(params: { hazardname: string; hazardscategoryname: string }): Promise<Hazard> {
  const res = await authFetch(`${baseUrl}/api/hazards`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.hazard;
}

function HazardSelect({ value, onChange, hazardsByCategory }: {
  value: string | number;
  onChange: (value: string) => void;
  hazardsByCategory: Map<string, Hazard[]>;
}) {
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value)} className="form-select">
      <option value="">-- Select --</option>
      {Array.from(hazardsByCategory.entries()).map(([cat, items]) => (
        <optgroup key={cat} label={cat}>
          {items.map(h => (
            <option key={h.dimhazards_id} value={h.dimhazards_id}>
              {h.hazardname}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export default function HazardMappingPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<CategorizationTab>('rasff');
  const [search, setSearch] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState('');
  const [page, setPage] = useState(0);
  const [pendingChanges, setPendingChanges] = useState<Map<number, number>>(new Map());
  const [bulkHazardId, setBulkHazardId] = useState('');
  const [showNewHazard, setShowNewHazard] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newError, setNewError] = useState('');

  const { data: hazards } = useQuery(['hazards'], fetchHazards);

  const rasffQuery = useQuery(
    ['uncategorized-notifications', page, searchSubmitted],
    () => fetchUncategorized(page * PAGE_SIZE, searchSubmitted),
    { keepPreviousData: true, enabled: activeTab === 'rasff' },
  );

  const alertsQuery = useQuery(
    ['uncategorized-alerts', page, searchSubmitted],
    () => fetchUncategorizedAlerts(page * PAGE_SIZE, searchSubmitted),
    { keepPreviousData: true, enabled: activeTab === 'food_alerts' },
  );

  const data = activeTab === 'rasff' ? rasffQuery.data : undefined;
  const alertsData = activeTab === 'food_alerts' ? alertsQuery.data : undefined;
  const isLoading = activeTab === 'rasff' ? rasffQuery.isLoading : alertsQuery.isLoading;
  const isError = activeTab === 'rasff' ? rasffQuery.isError : alertsQuery.isError;

  const mutation = useMutation(updateHazard, {
    onSuccess: (_data, variables) => {
      setPendingChanges(prev => {
        const next = new Map(prev);
        next.delete(variables.rasffId);
        return next;
      });
      queryClient.invalidateQueries(['uncategorized-notifications']);
      queryClient.invalidateQueries(['uncategorized-count']);
    },
  });

  const alertMutation = useMutation(updateAlertHazard, {
    onSuccess: (_data, variables) => {
      setPendingChanges(prev => {
        const next = new Map(prev);
        next.delete(variables.alertId);
        return next;
      });
      queryClient.invalidateQueries(['uncategorized-alerts']);
      queryClient.invalidateQueries(['uncategorized-count']);
    },
  });

  const createMutation = useMutation(createHazard, {
    onSuccess: () => {
      queryClient.invalidateQueries(['hazards']);
      setNewName('');
      setNewCategory('');
      setNewError('');
      setShowNewHazard(false);
    },
    onError: (err: Error) => {
      setNewError(err.message || 'Failed to create hazard');
    },
  });

  const hazardsByCategory = useMemo(() => {
    if (!hazards) return new Map<string, Hazard[]>();
    const map = new Map<string, Hazard[]>();
    for (const h of hazards) {
      const cat = h.hazardscategoryname || 'Other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(h);
    }
    return map;
  }, [hazards]);

  const categories = useMemo(() => {
    if (!hazards) return [];
    const set = new Set<string>();
    for (const h of hazards) {
      if (h.hazardscategoryname) set.add(h.hazardscategoryname);
    }
    return Array.from(set).sort();
  }, [hazards]);

  const handleSearch = useCallback(() => {
    setPage(0);
    setSearchSubmitted(search);
  }, [search]);

  function handleSelectChange(rasffId: number, value: string) {
    const dimId = parseInt(value, 10);
    if (isNaN(dimId)) return;
    setPendingChanges(prev => new Map(prev).set(rasffId, dimId));
  }

  function handleSave(itemId: number) {
    const dimId = pendingChanges.get(itemId);
    if (dimId === undefined) return;
    if (activeTab === 'rasff') {
      mutation.mutate({ rasffId: itemId, dimhazards_id: dimId });
    } else {
      alertMutation.mutate({ alertId: itemId, dimhazards_id: dimId });
    }
  }

  function handleSaveAll() {
    for (const [itemId, dimId] of pendingChanges) {
      if (activeTab === 'rasff') {
        mutation.mutate({ rasffId: itemId, dimhazards_id: dimId });
      } else {
        alertMutation.mutate({ alertId: itemId, dimhazards_id: dimId });
      }
    }
  }

  function handleBulkApply() {
    const dimId = parseInt(bulkHazardId, 10);
    if (isNaN(dimId)) return;
    const next = new Map(pendingChanges);
    if (activeTab === 'rasff') {
      for (const n of notifications) next.set(n.rasff_id, dimId);
    } else {
      for (const a of alerts) next.set(a.food_alerts_id, dimId);
    }
    setPendingChanges(next);
  }

  function handleCreateHazard() {
    if (!newName.trim()) { setNewError('Name is required'); return; }
    setNewError('');
    createMutation.mutate({ hazardname: newName.trim(), hazardscategoryname: newCategory.trim() });
  }

  const notifications = data?.notifications || [];
  const alerts = alertsData?.alerts || [];
  const total = activeTab === 'rasff' ? (data?.total || 0) : (alertsData?.total || 0);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  function handleTabSwitch(tab: CategorizationTab) {
    setActiveTab(tab);
    setPage(0);
    setPendingChanges(new Map());
    setBulkHazardId('');
  }

  return (
    <div className="container admin-page">
      <PageHeader
        title="Hazard Mapping"
        tagline="Assign BRC v9 hazard categories to uncategorised notifications and food alerts."
        back={{ to: '/admin', label: 'Back to Manage' }}
        actions={
          total > 0 ? <span className="card-badge">{total} uncategorised</span> : undefined
        }
      />

      <section className="card admin-section">
        <div className="card-header">
          <h3>Queue</h3>
        </div>
        <div className="card-body admin-section__body">
          <div className="news-page__tabs">
            <button
              className={`news-page__tab ${activeTab === 'rasff' ? 'news-page__tab--active' : ''}`}
              onClick={() => handleTabSwitch('rasff')}
            >RASFF</button>
            <button
              className={`news-page__tab ${activeTab === 'food_alerts' ? 'news-page__tab--active' : ''}`}
              onClick={() => handleTabSwitch('food_alerts')}
            >Food Alerts (FSA / FDA)</button>
          </div>

          <div className="admin-toolbar">
            <input
              type="text"
              placeholder="Search subject, hazard, reference, or category…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="form-input admin-toolbar__search"
            />
            <button className="btn btn--secondary" onClick={handleSearch}>Search</button>
            <button className="btn btn--secondary" onClick={() => setShowNewHazard(v => !v)}>
              + New hazard
            </button>
            {pendingChanges.size > 0 && (
              <button className="btn btn--primary" onClick={handleSaveAll}>
                Save all ({pendingChanges.size})
              </button>
            )}
          </div>

          {showNewHazard && (
            <div className="admin-inline-form">
              <div className="admin-inline-form__title">Create new hazard</div>
              <div className="admin-inline-form__row">
                <input
                  type="text"
                  placeholder="Hazard name"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  className="form-input"
                  onKeyDown={e => e.key === 'Enter' && handleCreateHazard()}
                />
                <select
                  value={newCategory}
                  onChange={e => setNewCategory(e.target.value)}
                  className="form-select"
                >
                  <option value="">-- Select category --</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                  className="btn btn--primary"
                  onClick={handleCreateHazard}
                  disabled={createMutation.isLoading}
                >
                  {createMutation.isLoading ? 'Creating…' : 'Create'}
                </button>
                <button
                  className="btn btn--ghost"
                  onClick={() => { setShowNewHazard(false); setNewError(''); }}
                >
                  Cancel
                </button>
              </div>
              {newError && <div className="admin-inline-form__error">{newError}</div>}
            </div>
          )}

          {isLoading && <p className="admin-muted">Loading…</p>}
          {isError && <p className="admin-error">Failed to load notifications</p>}

          {!isLoading && activeTab === 'rasff' && notifications.length === 0 && (
            <p className="admin-empty">No uncategorised RASFF notifications found.</p>
          )}
          {!isLoading && activeTab === 'food_alerts' && alerts.length === 0 && (
            <p className="admin-empty">No uncategorised food alerts found.</p>
          )}

          {((activeTab === 'rasff' && notifications.length > 0) || (activeTab === 'food_alerts' && alerts.length > 0)) && (
            <>
              <div className="admin-bulk">
                <span className="admin-bulk__label">Assign to all visible:</span>
                <HazardSelect
                  value={bulkHazardId}
                  onChange={setBulkHazardId}
                  hazardsByCategory={hazardsByCategory}
                />
                <button
                  className="btn btn--secondary"
                  onClick={handleBulkApply}
                  disabled={!bulkHazardId}
                >
                  Apply to {activeTab === 'rasff' ? notifications.length : alerts.length} rows
                </button>
              </div>

              <div className="data-table__wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      {activeTab === 'rasff' ? (
                        <>
                          <th>Reference</th>
                          <th>Date</th>
                          <th>Subject</th>
                          <th>Product</th>
                          <th>Hazard text</th>
                        </>
                      ) : (
                        <>
                          <th>Source</th>
                          <th>Date</th>
                          <th>Title</th>
                          <th>Type</th>
                          <th>Description</th>
                        </>
                      )}
                      <th>Current</th>
                      <th>Assign category</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeTab === 'rasff' && notifications.map(n => {
                      const hasChange = pendingChanges.has(n.rasff_id);
                      return (
                        <tr key={n.rasff_id} className={hasChange ? 'data-table__row--changed' : ''}>
                          <td>
                            <a
                              href={`https://webgate.ec.europa.eu/rasff-window/screen/notification/${n.notif_id}`}
                              target="_blank" rel="noreferrer"
                              className="admin-link"
                            >
                              {n.reference || n.notif_id}
                            </a>
                          </td>
                          <td className="admin-muted admin-cell--nowrap">
                            {n.ec_validation_date ? new Date(n.ec_validation_date).toLocaleDateString('en-GB') : '-'}
                          </td>
                          <td className="admin-cell--primary">{n.subject}</td>
                          <td className="admin-muted">{n.product_category || '-'}</td>
                          <td className="admin-muted">{n.hazard_names || '-'}</td>
                          <td>
                            <span className="source-badge">
                              {n.dim_hazards?.hazardname || 'Unknown'}
                            </span>
                          </td>
                          <td>
                            <HazardSelect
                              value={pendingChanges.get(n.rasff_id) ?? ''}
                              onChange={v => handleSelectChange(n.rasff_id, v)}
                              hazardsByCategory={hazardsByCategory}
                            />
                          </td>
                          <td>
                            {hasChange && (
                              <button
                                className="btn btn--primary btn--sm"
                                onClick={() => handleSave(n.rasff_id)}
                                disabled={mutation.isLoading}
                              >
                                Save
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {activeTab === 'food_alerts' && alerts.map(a => {
                      const hasChange = pendingChanges.has(a.food_alerts_id);
                      const sourceLabel = a.source === 'fsa_uk' ? 'UK FSA' : a.source === 'openfda' ? 'US FDA' : a.source;
                      return (
                        <tr key={a.food_alerts_id} className={hasChange ? 'data-table__row--changed' : ''}>
                          <td>{sourceLabel}</td>
                          <td className="admin-muted admin-cell--nowrap">
                            {a.published_date ? new Date(a.published_date).toLocaleDateString('en-GB') : '-'}
                          </td>
                          <td className="admin-cell--primary">{a.title}</td>
                          <td className="admin-muted">{a.alert_type || '-'}</td>
                          <td className="admin-muted">{a.description ? a.description.substring(0, 150) : '-'}</td>
                          <td>
                            <span className="source-badge">
                              {a.dim_hazards?.hazardname || 'Unknown'}
                            </span>
                          </td>
                          <td>
                            <HazardSelect
                              value={pendingChanges.get(a.food_alerts_id) ?? ''}
                              onChange={v => handleSelectChange(a.food_alerts_id, v)}
                              hazardsByCategory={hazardsByCategory}
                            />
                          </td>
                          <td>
                            {hasChange && (
                              <button
                                className="btn btn--primary btn--sm"
                                onClick={() => handleSave(a.food_alerts_id)}
                                disabled={alertMutation.isLoading}
                              >
                                Save
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="admin-footer">
            <div className="admin-pagination">
              <button
                className="btn btn--ghost btn--sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                ← Previous
              </button>
              <span className="admin-muted">Page {page + 1} of {totalPages || 1}</span>
              <button
                className="btn btn--ghost btn--sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Next →
              </button>
            </div>
            {pendingChanges.size > 0 && (
              <span className="admin-muted">{pendingChanges.size} unsaved changes</span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
